import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router';
import { BlueskyIcon } from '../components/BlueskyIcon';
import { useDebouncedUrlValue } from '../hooks/useDebouncedUrlValue';
import { useUrlState } from '../hooks/useUrlState';
import { apiClient } from '../lib/api-client';
import { formatDateTime } from '../utils/datetime';

interface User {
  id: string;
  did: string;
  handle: string;
  display_name: string | null;
  avatar: string | null;
  created_at: string;
  status: string;
  is_active: boolean;
}

// 1 ページあたりの件数と先頭ページ番号（0 始まり）。
const PAGE_SIZE = 20;
const FIRST_PAGE = 0;
// URL に不正なステータスが入った場合のフォールバック用の許可値。
const STATUS_FILTERS = ['', 'ACTIVE', 'SUSPENDED', 'BLOCKED'] as const;

export default function UsersPage() {
  const [users, setUsers] = useState<User[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // ページ・フィルタ・検索は URL クエリで管理し、戻る / 進む と URL 共有に対応する
  const [{ page, status: statusFilter, search }, setUrlState] = useUrlState(
    { page: FIRST_PAGE, status: '', search: '' },
    { status: STATUS_FILTERS }
  );

  // 入力中の値はローカルに保持し、デバウンス後に URL へ反映する。
  // 1 文字ごとに履歴が増えないよう置き換えで反映し、ページは先頭へ戻す。
  const commitSearch = useCallback(
    (next: string) => setUrlState({ search: next, page: FIRST_PAGE }, { replace: true }),
    [setUrlState]
  );
  const [searchText, setSearchText] = useDebouncedUrlValue(search, commitSearch);

  // Load users when page, filter, or search changes
  useEffect(() => {
    void loadUsers();
  }, [page, statusFilter, search]);

  const loadUsers = async () => {
    setLoading(true);
    setError('');

    try {
      const data = await apiClient.getUsers({
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
        status: statusFilter || undefined,
        search: search || undefined
      });
      setUsers(data.users);
      setTotal(data.total);
    } catch (_err) {
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);

  const getStatusBadge = (status: string) => {
    const colors = {
      ACTIVE: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
      SUSPENDED: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
      BLOCKED: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
    };
    return (
      colors[status as keyof typeof colors] ||
      'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
    );
  };

  return (
    <div>
      <h1 className='mb-6 text-3xl font-bold text-gray-800 dark:text-white'>Users</h1>

      {/* Filters */}
      <div className='mb-4 flex flex-col gap-4 md:flex-row'>
        <input
          type='text'
          value={searchText}
          onChange={e => setSearchText(e.target.value)}
          placeholder='Search by name, handle, or DID...'
          className='flex-1 rounded-md border border-gray-300 bg-white px-3 py-2 dark:border-gray-600 dark:bg-gray-700 dark:text-white'
        />
        <select
          value={statusFilter}
          onChange={e => setUrlState({ status: e.target.value, page: FIRST_PAGE })}
          className='rounded-md border border-gray-300 bg-white px-3 py-2 md:w-auto dark:border-gray-600 dark:bg-gray-700 dark:text-white'
        >
          <option value=''>All Statuses</option>
          <option value='ACTIVE'>Active</option>
          <option value='SUSPENDED'>Suspended</option>
          <option value='BLOCKED'>Blocked</option>
        </select>
      </div>

      {error && (
        <div className='mb-4 rounded-md bg-red-50 p-4 text-red-800 dark:bg-red-900/20 dark:text-red-200'>
          {error}
        </div>
      )}

      {/* Users Table - Desktop Only */}
      <div className='hidden overflow-hidden rounded-lg bg-white shadow-md md:block dark:bg-gray-800'>
        <table className='min-w-full divide-y divide-gray-200 dark:divide-gray-700'>
          <thead className='bg-gray-50 dark:bg-gray-700'>
            <tr>
              <th className='px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase dark:text-gray-300'>
                User
              </th>
              <th className='px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase dark:text-gray-300'>
                Handle
              </th>
              <th className='px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase dark:text-gray-300'>
                Status
              </th>
              <th className='px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase dark:text-gray-300'>
                Created At
              </th>
              <th className='px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase dark:text-gray-300'>
                Actions
              </th>
            </tr>
          </thead>
          <tbody className='divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-800'>
            {loading ? (
              <tr>
                <td colSpan={5} className='px-6 py-4 text-center dark:text-gray-300'>
                  Loading...
                </td>
              </tr>
            ) : users.length === 0 ? (
              <tr>
                <td colSpan={5} className='px-6 py-4 text-center text-gray-500 dark:text-gray-400'>
                  No users found
                </td>
              </tr>
            ) : (
              users.map(user => (
                <tr key={user.id}>
                  <td className='px-6 py-4 whitespace-nowrap'>
                    <Link
                      to={`/users/${user.id}`}
                      className='flex cursor-pointer items-center transition-opacity hover:opacity-80'
                    >
                      <div className='h-10 w-10 flex-shrink-0'>
                        {user.avatar ? (
                          <img
                            className='h-10 w-10 rounded-full'
                            src={user.avatar}
                            alt={user.display_name || user.handle}
                          />
                        ) : (
                          <div className='flex h-10 w-10 items-center justify-center rounded-full bg-gray-300 text-gray-600 dark:bg-gray-600 dark:text-gray-300'>
                            {(user.display_name || user.handle || '?')[0].toUpperCase()}
                          </div>
                        )}
                      </div>
                      <div className='ml-4'>
                        <div className='text-sm font-medium text-gray-900 dark:text-white'>
                          {user.display_name ?? '-'}
                        </div>
                        <div className='text-xs text-gray-500 dark:text-gray-400'>{user.did}</div>
                      </div>
                    </Link>
                  </td>
                  <td className='px-6 py-4 whitespace-nowrap'>
                    <div className='flex items-center gap-2'>
                      <span className='text-sm text-gray-900 dark:text-white'>@{user.handle}</span>
                      <a
                        href={`https://bsky.app/profile/${user.handle}`}
                        target='_blank'
                        rel='noopener noreferrer'
                        className='text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300'
                        title='View on Bluesky'
                        onClick={e => e.stopPropagation()}
                      >
                        <BlueskyIcon />
                      </a>
                    </div>
                  </td>
                  <td className='px-6 py-4 whitespace-nowrap'>
                    <span
                      className={`inline-flex rounded-full px-2 text-xs leading-5 font-semibold ${getStatusBadge(user.status)}`}
                    >
                      {user.status}
                    </span>
                  </td>
                  <td className='px-6 py-4 text-sm whitespace-nowrap text-gray-500 dark:text-gray-400'>
                    {formatDateTime(user.created_at, { dateStyle: 'medium', timeStyle: 'short' }) ||
                      'N/A'}
                  </td>
                  <td className='px-6 py-4 text-sm font-medium whitespace-nowrap'>
                    <Link
                      to={`/users/${user.id}`}
                      className='text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300'
                    >
                      View Details
                    </Link>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Users Cards - Mobile Only */}
      <div className='space-y-4 md:hidden'>
        {loading ? (
          <div className='rounded-lg bg-white p-6 text-center shadow-md dark:bg-gray-800 dark:text-gray-300'>
            Loading...
          </div>
        ) : users.length === 0 ? (
          <div className='rounded-lg bg-white p-6 text-center text-gray-500 shadow-md dark:bg-gray-800 dark:text-gray-400'>
            No users found
          </div>
        ) : (
          users.map(user => (
            <Link
              key={user.id}
              to={`/users/${user.id}`}
              className='block rounded-lg bg-white p-4 shadow-md transition-shadow hover:shadow-lg dark:bg-gray-800'
            >
              {/* User Info */}
              <div className='mb-3 flex items-center gap-3'>
                {user.avatar ? (
                  <img
                    className='h-12 w-12 rounded-full'
                    src={user.avatar}
                    alt={user.display_name || user.handle}
                  />
                ) : (
                  <div className='flex h-12 w-12 items-center justify-center rounded-full bg-gray-300 text-gray-600 dark:bg-gray-600 dark:text-gray-300'>
                    {(user.display_name || user.handle || '?')[0].toUpperCase()}
                  </div>
                )}
                <div className='min-w-0 flex-1'>
                  <div className='truncate text-base font-medium text-gray-900 dark:text-white'>
                    {user.display_name ?? '-'}
                  </div>
                  <div className='flex items-center gap-2 truncate text-sm text-gray-500 dark:text-gray-400'>
                    <span>@{user.handle}</span>
                    <a
                      href={`https://bsky.app/profile/${user.handle}`}
                      target='_blank'
                      rel='noopener noreferrer'
                      className='text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300'
                      title='View on Bluesky'
                      onClick={e => e.stopPropagation()}
                    >
                      <BlueskyIcon />
                    </a>
                  </div>
                </div>
                <span
                  className={`flex-shrink-0 rounded-full px-2.5 py-0.5 text-xs font-semibold ${getStatusBadge(user.status)}`}
                >
                  {user.status}
                </span>
              </div>

              {/* Additional Info */}
              <div className='space-y-1 border-t border-gray-200 pt-3 dark:border-gray-700'>
                <div className='flex justify-between text-sm'>
                  <span className='text-gray-500 dark:text-gray-400'>DID:</span>
                  <span className='truncate pl-2 font-mono text-xs text-gray-900 dark:text-white'>
                    {user.did}
                  </span>
                </div>
                <div className='flex justify-between text-sm'>
                  <span className='text-gray-500 dark:text-gray-400'>Created:</span>
                  <span className='text-gray-900 dark:text-white'>
                    {formatDateTime(user.created_at, { dateStyle: 'medium', timeStyle: 'short' })}
                  </span>
                </div>
              </div>
            </Link>
          ))
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className='mt-4 flex flex-col items-center gap-3 md:flex-row md:justify-between'>
          <div className='text-sm text-gray-700 dark:text-gray-300'>
            Showing {page * PAGE_SIZE + 1} to {Math.min((page + 1) * PAGE_SIZE, total)} of {total}{' '}
            users
          </div>
          <div className='flex gap-2'>
            <button
              onClick={() => setUrlState({ page: Math.max(FIRST_PAGE, page - 1) })}
              disabled={page === FIRST_PAGE}
              className='rounded-md bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600 dark:disabled:bg-gray-800 dark:disabled:text-gray-600'
            >
              Previous
            </button>
            <button
              onClick={() => setUrlState({ page: Math.min(totalPages - 1, page + 1) })}
              disabled={page >= totalPages - 1}
              className='rounded-md bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600 dark:disabled:bg-gray-800 dark:disabled:text-gray-600'
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
